import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Switch, Text, View, useWindowDimensions } from 'react-native';
import { MapPin, Pencil, Plus, Search, Trash2 } from 'lucide-react-native';

import { Button, Card, EmptyState, SkeletonRows, Sheet, TextField } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  areasQuery,
  crearArea,
  actualizarArea,
  eliminarArea,
  slugifyArea,
  type Area,
} from '@/services/firebase/areas';
import { colors, radius, shadows, typography } from '@/theme/tokens';
import { normalizar } from '@/utils/format';

export function AreasScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;
  const cols = width >= 1320 ? 3 : width >= 760 ? 2 : 1;
  const { data, loading } = useFirestoreCollection(areasQuery);
  const areas = data as Area[];

  const [buscar, setBuscar] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editando, setEditando] = useState<Area | null>(null);

  // Cambios optimistas: el switch se ve al instante y el guardado va por detrás.
  // En modo API los datos se refrescan cada ~5s; sin esto el toggle tardaría en
  // reflejarse. Cada override se limpia solo cuando el server ya devuelve ese valor.
  const [overrides, setOverrides] = useState<Record<string, Partial<Area>>>({});

  const merged = useMemo(
    () => areas.map((a) => (overrides[a.slug] ? { ...a, ...overrides[a.slug] } : a)),
    [areas, overrides],
  );

  useEffect(() => {
    setOverrides((prev) => {
      const slugs = Object.keys(prev);
      if (slugs.length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      for (const a of areas) {
        const o = next[a.slug];
        if (o && Object.entries(o).every(([k, v]) => (a as any)[k] === v)) {
          delete next[a.slug];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [areas]);

  const activas = merged.filter((a) => a.activa !== false).length;
  const pedibles = merged.filter((a) => a.activa !== false && a.pedible !== false).length;

  const filtradas = useMemo(() => {
    const q = normalizar(buscar);
    if (!q) return merged;
    return merged.filter((a) => normalizar(a.nombre).includes(q) || normalizar(a.descripcion).includes(q));
  }, [merged, buscar]);

  const abrirNueva = () => {
    setEditando(null);
    setEditorOpen(true);
  };
  const abrirEditar = (a: Area) => {
    setEditando(a);
    setEditorOpen(true);
  };

  const aplicarOptimista = (slug: string, patch: Partial<Area>) => {
    setOverrides((p) => ({ ...p, [slug]: { ...p[slug], ...patch } }));
    actualizarArea(slug, patch).catch(() => {
      // si falla, descartamos el override para volver a lo que diga el server
      setOverrides((p) => {
        const n = { ...p };
        delete n[slug];
        return n;
      });
    });
  };

  const toggleActiva = (a: Area) => aplicarOptimista(a.slug, { activa: !(a.activa !== false) });
  const togglePedible = (a: Area) => aplicarOptimista(a.slug, { pedible: !(a.pedible !== false) });

  const onEliminar = async (a: Area) => {
    await eliminarArea(a.slug);
  };

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 16, paddingBottom: 90 }}>
      {/* Encabezado */}
      <View
        style={{
          flexDirection: wide ? 'row' : 'column',
          alignItems: wide ? 'center' : 'stretch',
          justifyContent: 'space-between',
          gap: 14,
          backgroundColor: colors.white,
          borderRadius: radius['3xl'],
          padding: wide ? 22 : 18,
          borderWidth: 1,
          borderColor: colors.ink[100],
          ...shadows.soft,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, flex: 1 }}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: radius.xl,
              backgroundColor: colors.magenta[50],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MapPin size={24} color={colors.magenta[600]} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: wide ? 22 : 19, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Áreas de la clínica
            </Text>
            <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 2 }}>
              {areas.length} en total · {activas} activas · {pedibles} para pedir insumos
            </Text>
          </View>
        </View>
        <Button label="Nueva área" iconLeft={<Plus size={16} color={colors.white} />} onPress={abrirNueva} />
      </View>

      {/* Buscador */}
      {!loading && areas.length > 0 ? (
        <View style={{ gap: 8 }}>
          {Platform.OS === 'web' ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                backgroundColor: colors.white,
                borderRadius: radius.full,
                borderWidth: 1.5,
                borderColor: colors.ink[200],
                paddingHorizontal: 16,
                height: 48,
              }}
            >
              <Search size={18} color={colors.ink[400]} />
              {/* @ts-ignore web-only native input */}
              <input
                type="text"
                value={buscar}
                onChange={(e: any) => setBuscar(e.target.value)}
                placeholder="Buscar área… (para ver si ya existe)"
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: colors.ink[900], fontFamily: 'inherit' }}
              />
              {buscar ? (
                <Pressable onPress={() => setBuscar('')} hitSlop={8} style={{ /* @ts-ignore */ cursor: 'pointer' }}>
                  <Text style={{ color: colors.ink[400], fontSize: 13, fontWeight: typography.weights.bold }}>Limpiar</Text>
                </Pressable>
              ) : null}
            </View>
          ) : (
            <TextField value={buscar} onChangeText={setBuscar} placeholder="Buscar área…" />
          )}
          <Text style={{ color: colors.ink[400], fontSize: 12.5, paddingHorizontal: 4 }}>
            {buscar.trim()
              ? `${filtradas.length} ${filtradas.length === 1 ? 'área' : 'áreas'} coinciden con «${buscar.trim()}»`
              : 'Las áreas marcadas “Se pide aquí” aparecen en el selector de las doctoras al armar una lista de insumos.'}
          </Text>
        </View>
      ) : null}

      {loading ? (
        <SkeletonRows rows={4} />
      ) : areas.length === 0 ? (
        <Card>
          <EmptyState
            illustration="note"
            title="Sin áreas todavía"
            message="Creá la primera área para que las doctoras puedan pedir insumos para ella."
          />
        </Card>
      ) : filtradas.length === 0 ? (
        <Card>
          <EmptyState
            illustration="cat"
            title="No existe esa área"
            message={`No hay ninguna área que coincida con «${buscar.trim()}». Podés crearla con el botón “Nueva área”.`}
          />
        </Card>
      ) : (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
          {filtradas.map((a) => (
            <View
              key={a.slug}
              style={{
                // grid fluido: 1 col en celu, 2-3 en PC
                flexGrow: 1,
                flexBasis: cols === 1 ? '100%' : cols === 2 ? '46%' : '30%',
                minWidth: cols === 1 ? '100%' : 280,
              }}
            >
              <AreaCard
                area={a}
                onEditar={() => abrirEditar(a)}
                onToggleActiva={() => toggleActiva(a)}
                onTogglePedible={() => togglePedible(a)}
              />
            </View>
          ))}
        </View>
      )}

      <AreaEditorSheet
        visible={editorOpen}
        onClose={() => setEditorOpen(false)}
        area={editando}
        existentes={areas}
        onEliminar={onEliminar}
      />
    </ScrollView>
  );
}

// ============================================================================
// Tarjeta de un área
// ============================================================================
function AreaCard({
  area,
  onEditar,
  onToggleActiva,
  onTogglePedible,
}: {
  area: Area;
  onEditar: () => void;
  onToggleActiva: () => void;
  onTogglePedible: () => void;
}) {
  const activa = area.activa !== false;
  const pedible = area.pedible !== false;

  return (
    <View
      style={{
        backgroundColor: colors.white,
        borderRadius: radius['2xl'],
        borderWidth: 1.5,
        borderColor: activa ? colors.ink[100] : colors.ink[200],
        padding: 16,
        gap: 12,
        height: '100%',
        opacity: activa ? 1 : 0.72,
        ...shadows.soft,
      }}
    >
      {/* Cabecera */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: radius.lg,
            backgroundColor: activa ? colors.magenta[50] : colors.ink[100],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MapPin size={20} color={activa ? colors.magenta[600] : colors.ink[400]} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 16 }} numberOfLines={1}>
            {area.nombre}
          </Text>
          <Text style={{ color: colors.ink[400], fontSize: 12 }} numberOfLines={1}>
            {area.descripcion?.trim() || 'Sin descripción'}
          </Text>
        </View>
        <Pressable
          onPress={onEditar}
          hitSlop={8}
          style={({ hovered }) => ({
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
            height: 34,
            paddingHorizontal: 12,
            borderRadius: radius.md,
            alignSelf: 'flex-start',
            backgroundColor: hovered ? colors.magenta[100] : colors.magenta[50],
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          <Pencil size={15} color={colors.magenta[700]} />
          <Text style={{ color: colors.magenta[700], fontSize: 13, fontWeight: typography.weights.bold }}>Editar</Text>
        </Pressable>
      </View>

      {/* Toggles */}
      <View style={{ gap: 2 }}>
        <ToggleRow
          label="Activa"
          hint={activa ? 'Visible y en uso' : 'Oculta del sistema'}
          value={activa}
          onChange={onToggleActiva}
        />
        <View style={{ height: 1, backgroundColor: colors.ink[100] }} />
        <ToggleRow
          label="Aparece en pedidos"
          hint={pedible ? 'La doctora la elige al pedir insumos' : 'No aparece al pedir insumos'}
          value={pedible}
          onChange={onTogglePedible}
          disabled={!activa}
        />
      </View>
    </View>
  );
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 8, opacity: disabled ? 0.5 : 1 }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 13.5, fontWeight: typography.weights.bold, color: colors.ink[800] }}>{label}</Text>
        <Text style={{ fontSize: 11.5, color: colors.ink[400] }}>{hint}</Text>
      </View>
      <Switch
        value={value}
        onValueChange={disabled ? undefined : onChange}
        disabled={disabled}
        trackColor={{ false: colors.ink[200], true: colors.magenta[400] }}
        thumbColor={colors.white}
        // @ts-ignore web prop
        activeThumbColor={colors.white}
      />
    </View>
  );
}

// ============================================================================
// Editor — crear, renombrar o eliminar un área
// ============================================================================
function AreaEditorSheet({
  visible,
  onClose,
  area,
  existentes,
  onEliminar,
}: {
  visible: boolean;
  onClose: () => void;
  area: Area | null;
  existentes: Area[];
  onEliminar: (a: Area) => Promise<void>;
}) {
  const esNueva = !area;
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [pedible, setPedible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setNombre(area?.nombre ?? '');
    setDescripcion(area?.descripcion ?? '');
    setPedible(area ? area.pedible !== false : true);
    setError(null);
    setSaving(false);
    setBorrando(false);
  }, [visible, area]);

  const slugPreview = useMemo(() => slugifyArea(nombre), [nombre]);

  const onGuardar = async () => {
    const nom = nombre.trim();
    if (!nom) {
      setError('Poné un nombre.');
      return;
    }
    if (esNueva && existentes.some((a) => a.slug === slugPreview)) {
      setError('Ya existe un área con ese nombre.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (esNueva) {
        await crearArea({ nombre: nom, descripcion: descripcion.trim() || undefined, pedible });
      } else {
        await actualizarArea(area!.slug, {
          nombre: nom,
          descripcion: descripcion.trim() || null,
          pedible,
        });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const onBorrar = async () => {
    if (!area) return;
    const ok =
      typeof window === 'undefined'
        ? true
        : window.confirm(`¿Eliminar el área "${area.nombre}"? Las solicitudes ya hechas no se tocan, solo deja de aparecer para pedir.`);
    if (!ok) return;
    setBorrando(true);
    setError(null);
    try {
      await onEliminar(area);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo eliminar.');
    } finally {
      setBorrando(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="sm"
      title={esNueva ? 'Nueva área' : `Editar ${area?.nombre}`}
      subtitle={esNueva ? 'Un lugar de la clínica para el que se piden insumos.' : 'Cambiá el nombre, la descripción o dónde se usa.'}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} disabled={saving || borrando} />
          <Button label={esNueva ? 'Crear área' : 'Guardar'} onPress={onGuardar} loading={saving} disabled={borrando} />
        </>
      }
    >
      <TextField label="Nombre" placeholder="Ej: Sala de rayos X" value={nombre} onChangeText={setNombre} />
      {esNueva && nombre.trim() ? (
        <Text style={{ fontSize: 12, color: colors.ink[400], marginTop: -6 }}>id: {slugPreview}</Text>
      ) : null}
      <TextField
        label="Descripción (opcional)"
        placeholder="Para qué es el área"
        value={descripcion}
        onChangeText={setDescripcion}
      />

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 12,
          borderRadius: radius.xl,
          backgroundColor: colors.crema[50],
          gap: 12,
        }}
      >
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 14, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
            Aparece en pedidos de insumos
          </Text>
          <Text style={{ fontSize: 12, color: colors.ink[500], marginTop: 2 }}>
            Activalo si la doctora va a pedir insumos para esta área (consultorios, quirófano…). Apagalo para áreas internas como recepción.
          </Text>
        </View>
        <Switch
          value={pedible}
          onValueChange={setPedible}
          trackColor={{ false: colors.ink[200], true: colors.magenta[400] }}
          thumbColor={colors.white}
        />
      </View>

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}

      {/* Eliminar — solo al editar un área existente */}
      {!esNueva ? (
        <View style={{ borderTopWidth: 1, borderTopColor: colors.ink[100], paddingTop: 14, marginTop: 4 }}>
          <Pressable
            onPress={onBorrar}
            disabled={borrando}
            style={({ hovered }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              paddingVertical: 11,
              borderRadius: radius.lg,
              borderWidth: 1.5,
              borderColor: colors.danger[500],
              backgroundColor: hovered ? colors.danger[50] : colors.white,
              opacity: borrando ? 0.6 : 1,
              // @ts-ignore web only
              cursor: borrando ? 'default' : 'pointer',
            })}
          >
            <Trash2 size={16} color={colors.danger[600]} />
            <Text style={{ color: colors.danger[600], fontSize: 14, fontWeight: typography.weights.bold }}>
              {borrando ? 'Eliminando…' : 'Eliminar esta área'}
            </Text>
          </Pressable>
        </View>
      ) : null}
    </Sheet>
  );
}
